// src/pages/Login.js
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { loginUser } from "../api/auth";
import { getCurrentUser } from "../api/users";
import ProfileCompletionModal from "../components/ProfileCompletionModal";
import "./Login.css";

export default function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // 'error' or 'success'
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [userData, setUserData] = useState(null);

  const onChange = (e) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");
    
    try {
      const response = await loginUser(formData.email, formData.password);
      
      setMessage("Login successful! Redirecting...");
      setMessageType("success");
      
      // Get full user profile to check if it's complete
      const userRole = response.profile?.user?.role;
      
      if (userRole === "partner") {
        // Check if partner profile is incomplete
        try {
          const fullProfile = await getCurrentUser();
          const partnerProfile = fullProfile.partner_profile;
          
          // Check if essential profile fields are missing
          const isProfileIncomplete = 
            !fullProfile.user?.current_location || 
            !partnerProfile?.age || 
            !partnerProfile?.gender;
          
          if (isProfileIncomplete) {
            // Show profile completion modal
            setUserData(fullProfile);
            setShowProfileCompletion(true);
            setIsLoading(false);
            return;
          }
        } catch (err) {
          console.error("Error fetching user profile:", err);
        }
      }
      
      // Redirect based on user role
      setTimeout(() => {
        if (userRole === "partner") {
          navigate("/find-partner");
        } else if (userRole === "agency") {
          navigate("/agencies");
        } else {
          navigate("/");
        }
      }, 1500);
      
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage = error.response?.data?.detail || "Login failed. Please check your credentials.";
      setMessage(errorMessage);
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  function handleProfileComplete() {
    setShowProfileCompletion(false);
    navigate("/find-partner");
  }

  function handleProfileSkip() {
    setShowProfileCompletion(false);
    navigate("/find-partner");
  }

  return (
    <div className="login-container">
      {showProfileCompletion && userData && (
        <ProfileCompletionModal
          user={userData}
          onClose={handleProfileSkip}
          onComplete={handleProfileComplete}
        />
      )}

      {/* Left Side - Image Section */}
      <div className="login-image-section">
        <div className="overlay"></div>
        <div className="image-content">
          <h1 className="brand-title">🌍 Go With Me Buddy</h1>
          <p className="brand-subtitle">Discover the world with like-minded travelers</p>
          <div className="features">
            <div className="feature-item">
              <span className="feature-icon">✈️</span>
              <span>Find Travel Partners</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🗺️</span>
              <span>Plan Amazing Trips</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📸</span>
              <span>Share Experiences</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form Section */}
      <div className="login-form-section">
        <div className="form-wrapper">
          <div className="form-header">
            <h2 className="form-title">Welcome Back!</h2>
            <p className="form-subtitle">Login to continue your journey</p>
          </div>

          <form onSubmit={onSubmit} className="login-form">
            <div className="input-group">
              <label htmlFor="email" className="input-label">Email Address</label>
              <div className="input-wrapper">
                <span className="input-icon">📧</span>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={onChange}
                  required
                  className="form-input"
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="password" className="input-label">Password</label>
              <div className="input-wrapper">
                <span className="input-icon">🔒</span>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={onChange}
                  required
                  className="form-input"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input type="checkbox" />
                <span>Remember me</span>
              </label>
              <Link to="/forgot-password" className="forgot-link">Forgot Password?</Link>
            </div>

            <button type="submit" className="submit-btn" disabled={isLoading}>
              <span>{isLoading ? "Logging in..." : "Login"}</span>
              {!isLoading && <span className="btn-icon">→</span>}
            </button>

            {message && (
              <div className={`message-box ${messageType}`}>
                <span className="message-icon">{messageType === 'error' ? '⚠️' : '✓'}</span>
                {message}
              </div>
            )}
          </form>

          <div className="divider">
            <span>or continue with</span>
          </div>

          <div className="social-login">
            <button className="social-btn google-btn">
              <span className="social-icon">G</span>
              Google
            </button>
            <button className="social-btn facebook-btn">
              <span className="social-icon">f</span>
              Facebook
            </button>
          </div>

          <p className="signup-prompt">
            Don't have an account? <Link to="/register" className="signup-link">Sign up</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
